// Simple functions to check the user's groups for certain permissions without having to hard code the number in each
// place. If for whatever reason, a permission group id changes, change it here or trouble will occur.
// eslint-disable-next-line no-restricted-imports -- pre-existing Immutable usage; migrate off when touched
import { List } from 'immutable';
const findOne = (haystack, needles) => needles.some(v => haystack.includes(v));

export const determineIsODLEngineering = groups => groups.indexOf(1) > -1;
export const determineIsQuickBooks = groups => groups.indexOf(1067) > -1;
export const determineIsManufacturing = groups => groups.indexOf(2) > -1;
export const determineIsShipping = groups => groups.indexOf(3) > -1;
export const determineIsAccounting = groups => groups.indexOf(4) > -1;
export const determineIsSales = groups => groups.indexOf(5) > -1 || groups.indexOf(1071) > -1;
export const determineIsSalesAdmin = groups => groups.indexOf(1071) > -1;
export const determineIsSalesPerson = groups => groups.indexOf(1072) > -1;
export const determineIsTechSupport = groups => groups.indexOf(6) > -1;
export const determineIsCustomerAdmin = groups => groups.indexOf(7) > -1;
export const determineIsSiteGroupAdmin = groups => groups.indexOf(8) > -1;
export const determineIsSiteGroupUser = groups => groups.indexOf(9) > -1;
export const determineIsScheduleUser = groups => groups.indexOf(10) > -1;
export const determineIsTechnician = groups => groups.indexOf(11) > -1;
export const determineIsReadOnlySiteGroupUser = groups => groups.indexOf(1054) > -1;
export const determineIsCrewManager = groups => groups.indexOf(1055) > -1;
export const determineIsResellerUser = groups => groups.indexOf(1061) > -1;
export const determineIsInventoryAdmin = groups => groups.indexOf(1066) > -1;

const groupsWithPrivilegedCommands = [1, 6]; // engineering and tech support
export const determineCanIssuePrivilegedCommands = groups => findOne(groups, groupsWithPrivilegedCommands);

// common time saver combos

// tech support, reseller user, customer admin, site group admin, site group user
export const determineIsAtLeastSiteGroupUser = groups => findOne(groups, [6, 1061, 7, 8, 9]);
// tech support, reseller user, customer admin, sitegroup admin
export const determineIsAtLeastSiteGroupAdmin = groups => findOne(groups, [6, 1061, 7, 8]);
// tech support, reseller user, customer admin
export const determineIsAtLeastCustomerAdmin = groups => findOne(groups, [6, 1061, 7]);

// pass in the role id, and if billboard or not. get out a string

/**
 * all roles
 *
 * 1 = Engineering
 * 2 = Manufacturing
 * 3 = Shipping
 * 4 = Accounting
 * 5 = Sales
 * 6 = Tech Support
 * 7 = Customer Admin
 * 8 = SiteGroup Admin
 * 9 = SiteGroup User
 * 10 = Schedule Control
 * 11 = Technicians
 * 1054 = Read Only User
 * 1055 = Crew Manager
 * 1056 = API Manager
 * 1057 = API user
 * 1059 = Relay Commands
 * 1060 = Intrusion Commands
 * 1061 = Reseller User
 *
 */

// Role ids that have a display name in roleDisplayNameSwitch. Groups outside
// this set are internal/permission-only and should not appear in the UI.
const KNOWN_ROLE_IDS = new Set([
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 1054, 1055, 1056, 1057, 1059, 1060, 1061, 1062, 1063, 1064, 1066, 1067, 1071, 1072,
]);

export const roleDisplayNameSwitch = (role, isBillboard = true, t = null) => {
  const getTranslation = (key, fallback) => (t ? t(key) : fallback);
  switch (role) {
    case 1:
      return getTranslation('role.engineering', 'Engineering');
    case 2:
      return getTranslation('role.manufacturing', 'Manufacturing');
    case 3:
      return getTranslation('role.shipping', 'Shipping');
    case 4:
      return getTranslation('role.accounting', 'Accounting');
    case 5:
      return getTranslation('role.sales', 'Sales');
    case 6:
      return getTranslation('role.techSupport', 'Tech Support');
    case 7:
      return getTranslation('role.administrator', 'Administrator');
    case 8:
      return getTranslation(
        isBillboard ? 'role.plantManager' : 'role.siteGroupManager',
        isBillboard ? 'Plant Manager' : 'Site Group Manager',
      );
    case 9:
      return getTranslation('role.user', 'User');
    case 10:
      return getTranslation('role.scheduleControl', 'Schedule Control');
    case 11:
      return getTranslation('role.technicians', 'Technicians');
    case 1054:
      return getTranslation('role.viewOnlyUser', 'View Only User');
    case 1055:
      return getTranslation('role.crewManager', 'Crew Manager');
    case 1056:
      return getTranslation('role.apiManager', 'API Manager');
    case 1057:
      return getTranslation('role.apiUser', 'API User');
    case 1059:
      return getTranslation('role.relayCommands', 'Relay Commands');
    case 1060:
      return getTranslation('role.intrusionCommands', 'Intrusion Commands');
    case 1061:
      return getTranslation('role.reseller', 'Reseller');
    case 1062:
      return getTranslation('role.addonBypassCurfew', 'Addon Bypass Curfew');
    case 1063:
      return getTranslation('role.thirdPartyVendorAdmin', 'Third Party Vendor Admin');
    case 1064:
      return getTranslation('role.relayManager', 'Relay Manager (On-Off)');
    case 1066:
      return getTranslation('role.inventoryAdmin', 'Inventory Admin');
    case 1067:
      return getTranslation('role.quickBooksUser', 'QuickBooks User');
    case 1071:
      return getTranslation('role.salesAdmin', 'Sales Admin');
    case 1072:
      return getTranslation('role.salesPerson', 'Sales Person');
    default:
      return getTranslation('role.noRole', 'No Role');
  }
};

export const displayRoles = (roles, isBillboard = true, showSubRoles = true, limit = 1, t = null) => {
  // Accept both the legacy flat-id contract (groups:[1,2,3]) and the
  // {id, type, name} reference shape the odl-app grid endpoints return.
  const roleIds = List(roles).map(role => (role && typeof role === 'object' ? role.id : role));
  let immutableRoles = roleIds.sort(); // sorted list from lowest to largest

  // if "view only" is in there but not the only role, then it is implied.
  if (immutableRoles.size > 1 && immutableRoles.indexOf(1054) > -1) {
    immutableRoles = immutableRoles.delete(immutableRoles.indexOf(1054));
  }

  // Drop internal/permission-only groups that have no user-facing display name.
  immutableRoles = immutableRoles.filter(id => KNOWN_ROLE_IDS.has(id));

  return immutableRoles
    .slice(0, limit) // only show limit roles
    .map(role => roleDisplayNameSwitch(role, isBillboard, t)) // convert to display
    .join(', '); // separate names
};

// expects users with flat groups, ie groups:[1,2,3] not groups:[{id:1...}]
export const userCanEditUser = (userA, userB) => {
  // if you are super then good, otherwise if they are super then bad.
  if (userA.get('is_superuser', false)) {
    return true;
  }
  if (userB.get('is_superuser', false)) {
    return false;
  }

  // if you are staff then good, otherwise if they are staff then bad.
  if (userA.get('is_staff', false)) {
    return true;
  }
  if (userB.get('is_staff', false)) {
    return false;
  }

  // expect groups to be just IDs
  const groupsA = List(userA.get('groups', [])).toArray();
  const groupsB = List(userB.get('groups', [])).toArray();

  // you are a reseller user, you can change anyone under you.
  if (determineIsResellerUser(groupsA)) {
    return true;
  }
  // user you are trying to edit is a reseller, so you can't change them.
  if (determineIsResellerUser(groupsB)) {
    return false;
  }

  // you are a customer admin, you can change anyone under you
  if (determineIsCustomerAdmin(groupsA)) {
    return true;
  }
  // user you are trying to edit is an admin, so you can't change them.
  if (determineIsCustomerAdmin(groupsB)) {
    return false;
  }

  // if user A is site-group manager then they can edit everyone same and below, otherwise no editing is allowed.
  return determineIsSiteGroupAdmin(groupsA);
};

/** Convenience function to check user permissions without * */
export const userPermCheck = (user, permission) =>
  user.get('permissions', []).indexOf(permission) > -1 || user.get('is_superuser', false);

/**
 * Whether the signed-in user holds a feature grant (`features` on the auth
 * payload: the user's own effectively-enabled feature associations plus their
 * customer's; the backend already folds staff-implied keys in).
 *
 * @param {Map} user - the Immutable auth user.
 * @param {string} featureKey - a Feature key, e.g. 'MODERN_UI'.
 * @returns {boolean}
 */
export const userHasFeature = (user, featureKey) => user.get('features', []).indexOf(featureKey) > -1;
